import React, { useEffect, useState } from "react";
import { Alert, CircularProgress } from "@mui/material";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import CameraswitchOutlinedIcon from "@mui/icons-material/CameraswitchOutlined";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CustomButton from "../../../../components/buttons/CustomButton";
import useCameraCapture from "../hooks/useCameraCapture";
import styles from "./CranialSteps.module.css";

const CAMERA_TIPS = [
  "Câmera diretamente acima da cabeça",
  "Cabeça inteira dentro do contorno",
  "Nariz alinhado à marca central",
  "Nariz e as duas orelhas visíveis",
  "Sem cabelo ou objetos cobrindo o contorno",
  "Boa iluminação, celular sem inclinar",
];

const DEFAULT_SIZE = { width: 300, height: 400 };

/**
 * Framing guide drawn in the video pixel space, so it matches the captured frame.
 * Head seen from above: taller than wide, nose pointing to the top.
 */
function CameraGuide({ width, height }) {
  const base = Math.min(width, height);
  const cx = width / 2;
  const cy = height / 2;
  const ry = base * 0.4;
  const rx = ry * 0.78;
  const stroke = Math.max(2, base * 0.006);
  const noseY = cy - ry;
  const arrow = base * 0.035;

  return (
    <svg className={styles.cameraOverlay} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {/* Safe margin */}
      <ellipse cx={cx} cy={cy} rx={rx * 1.14} ry={ry * 1.12} fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth={stroke} strokeDasharray={`${stroke * 4} ${stroke * 3}`} />
      {/* Head guide */}
      <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="none" stroke="#FFFFFF" strokeWidth={stroke * 1.4} />
      {/* Anteroposterior axis */}
      <line x1={cx} y1={cy - ry} x2={cx} y2={cy + ry} stroke="rgba(255,255,255,0.55)" strokeWidth={stroke} strokeDasharray={`${stroke * 3} ${stroke * 3}`} />
      {/* Center crosshair */}
      <line x1={cx - base * 0.03} y1={cy} x2={cx + base * 0.03} y2={cy} stroke="#FFFFFF" strokeWidth={stroke} />
      <line x1={cx} y1={cy - base * 0.03} x2={cx} y2={cy + base * 0.03} stroke="#FFFFFF" strokeWidth={stroke} />
      {/* Nose marker */}
      <polygon
        points={`${cx},${noseY - arrow * 2.2} ${cx - arrow},${noseY - arrow * 0.6} ${cx + arrow},${noseY - arrow * 0.6}`}
        fill="#5A80FB"
        stroke="#FFFFFF"
        strokeWidth={stroke * 0.6}
      />
      <text x={cx} y={noseY - arrow * 2.8} textAnchor="middle" fill="#FFFFFF" fontSize={base * 0.04} fontFamily="Inter, sans-serif" fontWeight="600">
        nariz para cima
      </text>
    </svg>
  );
}

/**
 * Guided capture: live preview with the framing guide and short instructions.
 */
function GuidedCamera({ onCapture, onCancel, onUseUpload }) {
  const { videoRef, status, error, canSwitch, videoSize, start, switchCamera, capture, handleLoadedMetadata } = useCameraCapture();
  const [captureError, setCaptureError] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);

  useEffect(() => {
    start();
  }, [start]);

  const size = videoSize || DEFAULT_SIZE;
  const ratio = size.width / size.height;

  const handleCapture = () => {
    setIsCapturing(true);
    setCaptureError(null);
    capture()
      .then((blob) => onCapture(blob))
      .catch((e) => setCaptureError(e && e.message ? e.message : "Não foi possível capturar a foto."))
      .finally(() => setIsCapturing(false));
  };

  return (
    <div className={styles.card}>
      <div className={styles.cameraWrapper}>
        {status === "error" && error ? (
          <Alert severity="warning" style={{ width: "100%", borderRadius: 12 }}>
            {error.message}
          </Alert>
        ) : (
          <div className={styles.cameraViewport} style={{ aspectRatio: `${size.width} / ${size.height}`, maxWidth: `calc(65vh * ${ratio})` }}>
            <video ref={videoRef} className={styles.cameraVideo} playsInline muted autoPlay onLoadedMetadata={handleLoadedMetadata} />
            {status === "active" && <CameraGuide width={size.width} height={size.height} />}
            {status === "starting" && (
              <div className={styles.cameraStatus}>
                <CircularProgress color="inherit" />
              </div>
            )}
          </div>
        )}

        {status !== "error" && (
          <div className={styles.chips}>
            {CAMERA_TIPS.map((tip) => (
              <span key={tip} className={styles.chip}>{tip}</span>
            ))}
          </div>
        )}

        {captureError && (
          <Alert severity="error" style={{ width: "100%", borderRadius: 12 }}>
            {captureError}
          </Alert>
        )}
      </div>

      <div className={styles.actions}>
        <CustomButton variant="outlined" style="primary" label="Cancelar" icon={<CloseIcon />} onClick={onCancel} />
        {status === "error" ? (
          <>
            <CustomButton variant="outlined" style="primary" label="Tentar novamente" onClick={() => start()} />
            <CustomButton variant="contained" style="primary" label="Enviar foto" icon={<FileUploadOutlinedIcon />} onClick={onUseUpload} />
          </>
        ) : (
          <>
            {canSwitch && (
              <CustomButton
                variant="outlined"
                style="primary"
                label="Trocar câmera"
                icon={<CameraswitchOutlinedIcon />}
                onClick={switchCamera}
                enabled={status === "active"}
              />
            )}
            <CustomButton
              variant="contained"
              style="primary"
              label="Capturar"
              icon={<CameraAltOutlinedIcon />}
              onClick={handleCapture}
              enabled={status === "active" && !isCapturing}
              loading={isCapturing}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default GuidedCamera;
